import type { Localization as MuiLocale } from '@mui/material/locale';
import {
  daDK,
  deDE,
  enUS,
  fiFI,
  frFR,
  nbNO,
  nlNL,
  ruRU,
  svSE,
} from '@mui/material/locale';
import {
  daDK as dataGridDaDK,
  deDE as dataGridDeDE,
  enUS as dataGridEnUS,
  fiFI as dataGridFiFI,
  frFR as dataGridFrFR,
  nbNO as dataGridNbNO,
  nlNL as dataGridNlNL,
  ruRU as dataGridRuRU,
  svSE as dataGridSvSE,
} from '@mui/x-data-grid-pro/locales';
import {
  daDK as pickersDaDK,
  deDE as pickersDeDE,
  enUS as pickersEnUS,
  fiFI as pickersFiFI,
  frFR as pickersFrFR,
  nbNO as pickersNbNO,
  nlNL as pickersNlNL,
  ruRU as pickersRuRU,
  svSE as pickersSvSE,
} from '@mui/x-date-pickers-pro/locales';
import type { Locale as DateFnLocale } from 'date-fns';
import {
  da as dateFnDa,
  de as dateFnDe,
  enGB as dateFnEn,
  fi as dateFnFi,
  nb as dateFnNb,
  nl as dateFnNl,
  ru as dateFnRu,
  sv as dateFnSv,
  fr as dateFnFr,
} from 'date-fns/locale';
import i18next from 'i18next';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type LanguageCode =
  | 'da'
  | 'de'
  | 'en'
  | 'fi'
  | 'nb'
  | 'nl'
  | 'ru'
  | 'sv'
  | 'fr';

type Locale = {
  mui: MuiLocale;
  dataGrid: typeof dataGridDaDK;
  pickers: typeof pickersDaDK;
  dateFn: DateFnLocale;
};

interface LocaleStore {
  lang: LanguageCode;
  locale: Locale;
  setLang: (lang: LanguageCode) => void;
}

export const localeMap: Record<LanguageCode, Locale> = {
  da: {
    mui: daDK,
    dataGrid: dataGridDaDK,
    pickers: pickersDaDK,
    dateFn: dateFnDa,
  },
  de: {
    mui: deDE,
    dataGrid: dataGridDeDE,
    pickers: pickersDeDE,
    dateFn: dateFnDe,
  },
  en: {
    mui: enUS,
    dataGrid: dataGridEnUS,
    pickers: pickersEnUS,
    dateFn: dateFnEn,
  },
  fi: {
    mui: fiFI,
    dataGrid: dataGridFiFI,
    pickers: pickersFiFI,
    dateFn: dateFnFi,
  },
  nb: {
    mui: nbNO,
    dataGrid: dataGridNbNO,
    pickers: pickersNbNO,
    dateFn: dateFnNb,
  },
  nl: {
    mui: nlNL,
    dataGrid: dataGridNlNL,
    pickers: pickersNlNL,
    dateFn: dateFnNl,
  },
  ru: {
    mui: ruRU,
    dataGrid: dataGridRuRU,
    pickers: pickersRuRU,
    dateFn: dateFnRu,
  },
  sv: {
    mui: svSE,
    dataGrid: dataGridSvSE,
    pickers: pickersSvSE,
    dateFn: dateFnSv,
  },
  fr: {
    mui: frFR,
    dataGrid: dataGridFrFR,
    pickers: pickersFrFR,
    dateFn: dateFnFr,
  },
} as const;

// TODO: Think about where to better place this?
export const supportedLanguages = Object.keys(localeMap) as LanguageCode[];

export const useLocaleStore = create<LocaleStore>()(
  devtools((set) => ({
    lang: 'en',
    locale: localeMap.en,
    setLang: (lang: LanguageCode) => {
      i18next.changeLanguage(lang);
      set({ locale: localeMap[lang], lang: lang }, undefined, 'locale/setLang');
    },
  })),
);
